import { memo, useState, useCallback } from 'react';

import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { IconCate02 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import { LnbV2Menu } from '@wconcept/wconcept-common/models/renewal/lnb';
import {
  LargeCategory,
  MiddleCategory,
  PcGnbCategoryInfo,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import { getVisibleMegaMenuItems } from './megaMenu';
import * as S from './style';

const THEME_MENU_TITLE = '테마전문관';
const QUICK_MENU_TITLE = '주요 서비스';

interface IProps {
  /** className */
  className?: string;
  /** category */
  category?: readonly PcGnbCategoryInfo[];
  themes?: readonly LnbV2Menu[];
  quickMenus?: readonly LnbV2Menu[];
  /** onClickMediumCategory */
  onClickMediumCategory: (pcGnbCategoryInfo: PcGnbCategoryInfo) => void;
  /** onClickLargeCategory */
  onClickLargeCategory: (
    pcGnbCategoryInfo: PcGnbCategoryInfo,
    largeCategory: LargeCategory
  ) => void;
  /** onClickMiddleCategory */
  onClickMiddleCategory: (
    pcGnbCategoryInfo: PcGnbCategoryInfo,
    largeCategory: LargeCategory,
    middleCategory: MiddleCategory
  ) => void;
  /** onClickLnbMenu */
  onClickLnbMenu?: (lnbData: LnbV2Menu, type: string) => void;
}

const CateAreaDesktop = ({
  className,
  category,
  themes,
  quickMenus,
  onClickMediumCategory,
  onClickLargeCategory,
  onClickMiddleCategory,
  onClickLnbMenu,
}: IProps) => {
  const [isGnbCateOpen, setIsGnbCateOpen] = useState(false);
  const [cateActiveMediumCd, setCateActiveMediumCd] = useState<string>();

  const onHoverCateBtn = useCallback(() => {
    document.dispatchEvent(new Event('mousedown'));
    document.getElementById('searchInpHeader')?.blur();
    setIsGnbCateOpen(true);
  }, []);

  return (
    <S.CateAreaDesktop
      className={classNameBind(['area-cate-menu', className || ''])}
      $cateCount={category?.length ?? 0}
      onMouseEnter={() => {
        setIsGnbCateOpen(true);
        setCateActiveMediumCd(category?.[0]?.mediumCd);
        onHoverCateBtn();
      }}
      onFocus={() => {
        onHoverCateBtn();
      }}
      onMouseLeave={() => {
        setIsGnbCateOpen(false);
        setCateActiveMediumCd(undefined);
      }}
    >
      <Button
        onFocus={() => {
          setIsGnbCateOpen(true);
          setCateActiveMediumCd(category?.[0]?.mediumCd);
          onHoverCateBtn();
        }}
        className="gnb-cate-btn"
        icon={<IconCate02 />}
      />

      {isGnbCateOpen && (
        <div className="gnb-cate-list">
          <div className="inner-gcl">
            <div className="cate-list">
              <ul className="list-dpt1">
                {category?.map((v, i) => (
                  <li
                    key={v.mediumCd}
                    onFocusCapture={e => {
                      if (cateActiveMediumCd !== v.mediumCd) {
                        e.preventDefault();
                        setCateActiveMediumCd(v.mediumCd);
                      }
                    }}
                    onMouseEnter={e => {
                      e.preventDefault();
                      setCateActiveMediumCd(v.mediumCd);
                    }}
                    className={
                      v.mediumCd === cateActiveMediumCd ? 'active' : ''
                    }
                    data-mediumcd={v.mediumCd}
                  >
                    <Button
                      className="title-dpt1"
                      onClick={() => {
                        onClickMediumCategory(v);
                        setIsGnbCateOpen(false);
                      }}
                    >
                      {v.mediumName}
                    </Button>
                    {v.largeCategoryList?.length > 0 && (
                      <ul className="list-dpt2">
                        {v.largeCategoryList.map((largeCategory, j) => (
                          <li key={largeCategory.categoryCd}>
                            <Button
                              className="title-dpt2"
                              onClick={() => {
                                onClickLargeCategory(v, largeCategory);
                                setIsGnbCateOpen(false);
                              }}
                            >
                              {largeCategory.categoryName}
                            </Button>
                            {largeCategory.middleCategoryList.length > 0 && (
                              <ul className="list-dpt3">
                                {largeCategory.middleCategoryList.map(
                                  (middleCategory, k) => (
                                    <li key={middleCategory.categoryCd}>
                                      <Button
                                        onClick={() => {
                                          onClickMiddleCategory(
                                            v,
                                            largeCategory,
                                            middleCategory
                                          );
                                          setIsGnbCateOpen(false);
                                        }}
                                      >
                                        {middleCategory.categoryName}
                                      </Button>
                                    </li>
                                  )
                                )}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="aside-list">
              {!!themes?.length && (
                <dl className="list">
                  <dt>{THEME_MENU_TITLE}</dt>
                  {getVisibleMegaMenuItems(themes).map(lnbTheme => (
                    <dd key={lnbTheme.id}>
                      <Button
                        onClick={() => {
                          onClickLnbMenu?.(lnbTheme, 'theme');
                        }}
                      >
                        {lnbTheme.menuTitle}
                      </Button>
                    </dd>
                  ))}
                </dl>
              )}
              {!!quickMenus?.length && (
                <dl className="list">
                  <dt>{QUICK_MENU_TITLE}</dt>
                  {getVisibleMegaMenuItems(quickMenus).map(lnbService => (
                    <dd
                      key={lnbService.id}
                      className={lnbService.pointYn === 'Y' ? 'dot' : ''}
                    >
                      <Button
                        onClick={() => {
                          onClickLnbMenu?.(lnbService, 'service');
                        }}
                      >
                        {lnbService.menuTitle}
                      </Button>
                    </dd>
                  ))}
                </dl>
              )}
            </div>
          </div>
        </div>
      )}
    </S.CateAreaDesktop>
  );
};

export default memo(CateAreaDesktop);
